import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  CONFETTI_COLORS,
  KEY_BURST_MS,
  KEY_BUTTON_SIZE,
  PARTICLE_COUNT,
  PARTICLE_MIN_SIZE,
  PARTICLE_MIN_SPEED,
  PARTICLE_SIZE_RANGE,
  PARTICLE_SPEED_RANGE,
} from "../constants";
import type { ParticleData, Position } from "../types";
import Particle from "./Particle";

interface KeyButtonProps {
  keyChar: string;
  position: Position;
  onClick?: () => void;
}

export interface KeyButtonHandle {
  _pop: () => void;
}

const KeyButtonInner = forwardRef<KeyButtonHandle, KeyButtonProps>(
  ({ keyChar, position, onClick }, ref) => {
    const [state, setState] = useState<"idle" | "pressed" | "gone">("idle");
    const [particles, setParticles] = useState<ParticleData[]>([]);
    const divRef = useRef<HTMLDivElement>(null);

    const handlePop = useCallback(() => {
      if (state !== "idle") return;
      setState("pressed");

      const newParticles = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
        const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
        const speed = PARTICLE_MIN_SPEED + Math.random() * PARTICLE_SPEED_RANGE;
        return {
          id: Date.now() + i,
          x: position.x,
          y: position.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color:
            CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          size: PARTICLE_MIN_SIZE + Math.random() * PARTICLE_SIZE_RANGE,
        };
      });
      setParticles(newParticles);

      setTimeout(() => {
        setState("gone");
      }, KEY_BURST_MS);
    }, [state, position]);

    useImperativeHandle(ref, () => ({ _pop: handlePop }), [handlePop]);

    if (state === "gone") return null;
    return (
      <>
        {particles.map((p) => (
          <Particle
            key={p.id}
            {...p}
            onDone={() => setParticles((ps) => ps.filter((x) => x.id !== p.id))}
          />
        ))}

        <div
          ref={divRef}
          className="bg-[#b9b9b9] rounded-[4px] p-1"
          style={{
            position: "absolute",
            left: position.x,
            top: position.y,
            transform: "translate(-50%, -50%)",
            zIndex: 30,
            animation:
              state === "pressed"
                ? "keyBurst 0.4s ease-out forwards"
                : "keyPulse 2.2s ease-in-out infinite",
            cursor: onClick ? "pointer" : "default",
          }}
          onClick={
            onClick
              ? () => {
                  handlePop();
                  onClick();
                }
              : undefined
          }
        >
          <div
            className="bg-white rounded-[4px] text-black/55 inter-font"
            style={{
              width: KEY_BUTTON_SIZE,
              height: KEY_BUTTON_SIZE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12), 0 0 15px rgba(0,200,255,0.15)",
              fontSize: 15,
              letterSpacing: 1,
              userSelect: "none",
            }}
          >
            {keyChar}
          </div>
        </div>
      </>
    );
  },
);

KeyButtonInner.displayName = "KeyButton";

export const KeyButton = memo(KeyButtonInner);
export default KeyButton;
